import { ChevronDown, ChevronsUpDown, Search } from 'lucide-react'
import { useEffect, useState, useRef } from 'react'
import { useController, UseControllerProps } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../ui/form'

export interface ISelectFormData {
  id: string | number | null
  title: string
  subtitle?: string
}

interface Props {
  name: string
  label?: string
  data: ISelectFormData[]
  notFoundMessage?: string
  onChange?: (_value: ISelectFormData) => void
  showDoubleChevron?: boolean
}

export function SelectForm(props: Props & UseControllerProps) {
  const {
    control,
    name,
    label,
    data,
    notFoundMessage,
    onChange,
    rules,
    showDoubleChevron = false,
  } = props
  const { fieldState, field } = useController(props)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [selected, setSelected] = useState<ISelectFormData | undefined>(
    data?.find((itm) => itm.id === field.value)
  )
  const [searchQuery, setSearchQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const isRequired =
    rules?.required !== undefined || rules?.validate !== undefined

  // Update selected when field value changes
  useEffect(() => {
    const newSelected = data?.find((itm) => itm.id === field.value)
    setSelected(newSelected)
  }, [field.value, data])

  const filteredData =
    data?.filter(
      (item) =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.subtitle?.toLowerCase().includes(searchQuery.toLowerCase())
    ) || []

  const handleSelect = (selectedItem: ISelectFormData) => {
    setIsPopoverOpen(false)
    setSearchQuery('')
    setSelected(selectedItem)
    if (onChange) {
      onChange(selectedItem)
    } else {
      field.onChange(selectedItem.id)
    }
  }
  return (
    <div className={'w-full'} ref={containerRef}>
      <FormField
        control={control}
        name={name}
        render={() => (
          <FormItem>
            {label && (
              <FormLabel>
                {label}
                {isRequired && ' (*)'}
              </FormLabel>
            )}
            <FormControl>
              <Popover onOpenChange={setIsPopoverOpen} open={isPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    aria-expanded={isPopoverOpen}
                    className="h-fit w-full justify-between font-normal"
                    role="combobox"
                    variant="outline"
                  >
                    {selected ? (
                      <div className="flex flex-col items-start">
                        <span>{selected.title}</span>
                        {selected.subtitle && (
                          <span className="text-xs text-muted-foreground">
                            {selected.subtitle}
                          </span>
                        )}
                      </div>
                    ) : (
                      'Seleccionar...'
                    )}
                    {showDoubleChevron ? (
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    ) : (
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                  <div className="px-3 pt-3">
                    <div className="relative mb-2">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        className="pl-9"
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Buscar..."
                        value={searchQuery}
                      />
                    </div>
                  </div>
                  <div
                    className="max-h-[200px] overflow-y-auto"
                    onWheel={(e) => {
                      const target = e.currentTarget
                      target.scrollTop += e.deltaY
                    }}
                  >
                    {filteredData.length > 0 ? (
                      filteredData.map((item) => (
                        <div
                          className="cursor-pointer p-2 hover:bg-muted"
                          key={item.id}
                          onClick={() => handleSelect(item)}
                        >
                          <p className="font-medium">{item.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {item.subtitle}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        {notFoundMessage ?? 'No se encontraron clientes.'}
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </FormControl>
            <FormMessage>{fieldState.error?.message}</FormMessage>
          </FormItem>
        )}
      />
    </div>
  )
}
